import { format, parseISO } from 'date-fns';
import { AlertCircle, CheckCircle, Wrench } from 'lucide-react';

import { IncidentStatus, IncidentSummary, IncidentType } from '@/types/service/serviceHealth';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { getIncidentTypeDisplay } from '@/utils/statusPageHelpers';

interface IncidentCardProps {
  incident: IncidentSummary;
  showFullDetails?: boolean;
}

const IncidentCard = ({ incident, showFullDetails = true }: IncidentCardProps) => {
  const isOngoing = incident.status === IncidentStatus.ONGOING;
  const isMaintenance = incident.incident_type === IncidentType.MAINTENANCE;

  const typeDisplay = getIncidentTypeDisplay(incident.incident_type);

  const getIcon = () => {
    if (isMaintenance) {
      return <Wrench className='h-5 w-5 text-blue-600' />;
    }
    if (isOngoing) {
      return <AlertCircle className='h-5 w-5 text-orange-600' />;
    }
    return <CheckCircle className='h-5 w-5 text-emerald-600' />;
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return format(date, "MMM dd, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  const getCardStyles = () => {
    if (!isOngoing) return '';
    if (isMaintenance) return 'border-l-4 border-l-blue-500';
    return 'border-l-4 border-l-orange-500';
  };

  return (
    <Card className={getCardStyles()}>
      <CardHeader className='pb-2 pt-4'>
        <div className='flex items-start gap-3'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='mt-0.5 cursor-help'>{getIcon()}</div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{typeDisplay.label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className='flex-1'>
            <div className='flex items-start justify-between gap-2'>
              <CardTitle className='text-base font-semibold leading-tight'>
                {incident.title}
              </CardTitle>
              <div className='flex gap-2 flex-shrink-0'>
                <Badge
                  variant={isOngoing ? 'destructive' : 'default'}
                  className={`text-xs ${!isOngoing ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                >
                  {isOngoing ? 'Ongoing' : isMaintenance ? 'Completed' : 'Resolved'}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      {showFullDetails && (
        <CardContent className='space-y-2 pb-4'>
          <p className='text-sm text-muted-foreground whitespace-pre-line'>
            {incident.description}
          </p>

          <div className='text-xs text-muted-foreground space-y-1'>
            <p>
              <span className='font-medium'>Started:</span> {formatDateTime(incident.start_time)}
            </p>
            {incident.end_time && (
              <p>
                <span className='font-medium'>{isMaintenance ? 'Completed:' : 'Resolved:'}</span>{' '}
                {formatDateTime(incident.end_time)}
              </p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default IncidentCard;
